import React, { memo } from 'react';

import { AVATAR_SIZES } from '@atlaskit/avatar';
import { cssMap, css } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import type { IconSize } from './templateIconTypes.ts';

type TemplateLegacyIconTemplate = {
	styleClass: string | null;
	itemModuleCompleteKey: string | null;
	blueprintModuleCompleteKey?: string | null;
	contentBlueprintId?: string | null;
};

type TemplateLegacyIconProps = {
	template: TemplateLegacyIconTemplate;
	size?: IconSize;
};

const smallBackgroundSize = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundSize: `${AVATAR_SIZES['small']}px ${AVATAR_SIZES['small']}px`,
});

const mediumBackgroundSize = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundSize: `${AVATAR_SIZES['medium']}px ${AVATAR_SIZES['medium']}px`,
});

const largeBackgroundSize = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundSize: `${AVATAR_SIZES['large']}px ${AVATAR_SIZES['large']}px`,
});

const smallSize = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${AVATAR_SIZES['small']}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${AVATAR_SIZES['small']}px`,
});
const mediumSize = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${AVATAR_SIZES['medium']}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${AVATAR_SIZES['medium']}px`,
});
const largeSize = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${AVATAR_SIZES['large']}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${AVATAR_SIZES['large']}px`,
});

const styles = cssMap({
	// We need to preserve the HTML structure from the Create Dialog for 3rd party blueprints that use a styleclass
	// for their icon, as the corresponding CSS selectors rely on this structure
	classIconWrapperStyles: {
		listStyleType: 'none',
	},

	classIconStyles: {
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
	},
	iconStyles: {
		boxSizing: 'border-box',
		margin: 0,
		marginRight: token('space.050'),
		padding: 0,
		flexShrink: 0,
		flexGrow: 0,
		alignSelf: 'flex-start',
		boxShadow: 'initial',
	},
});

const backgroundSizeMapping = {
	small: smallBackgroundSize,
	medium: mediumBackgroundSize,
	large: largeBackgroundSize,
} as const;

const sizeMapping = {
	small: smallSize,
	medium: mediumSize,
	large: largeSize,
} as const;

// The avatar-item component, which actually renders TemplateLegacyIcon, passes a backgroundColor
// prop to the TemplateLegacyIcon. The TemplateLegacyIcon disregards this backgroundColor
// prop. Therefore, we can ignore the backgroundColor prop for memoization.
const areEqual = (prevProps: TemplateLegacyIconProps, nextProps: TemplateLegacyIconProps) => {
	return prevProps.template === nextProps.template;
};

export const TemplateLegacyIcon = memo(({ template, size = 'medium' }: TemplateLegacyIconProps) => {
	const styleClass = template.styleClass || '';
	const itemModuleCompleteKey = template.itemModuleCompleteKey || '';
	const blueprintModuleCompleteKey = template.blueprintModuleCompleteKey || '';
	const contentBlueprintId = template.contentBlueprintId || '';

	return (
		<li
			css={[styles.iconStyles, styles.classIconWrapperStyles]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="template"
			data-item-module-complete-key={itemModuleCompleteKey}
			data-blueprint-module-complete-key={blueprintModuleCompleteKey}
			data-content-blueprint-id={contentBlueprintId}
			data-testid="template-icon-legacy"
		>
			<span
				css={[
					styles.iconStyles,
					sizeMapping[size],
					backgroundSizeMapping[size],
					styles.classIconStyles,
				]}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={styleClass}
			/>
		</li>
	);
}, areEqual);
